import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Device } from 'mediasoup-client';
import { leaveOtherGroupCallSessions, registerGroupCallSession } from '../utils/groupCallSession';
import {
  clearActiveCall,
  consumeActiveCall,
  isPageUnloading,
  readActiveCall,
  saveActiveCall,
  waitForSocket,
} from '../utils/activeCallPersistence';

/**
 * Group audio/video calls over a mediasoup SFU.
 *
 * Every participant publishes their tracks once to the server and consumes one
 * stream per remote participant. There is no peer-to-peer mesh anywhere in this
 * path, so the number of uploads stays at one regardless of call size.
 *
 * Call lifecycle:
 *   start   -> sfuGroupCallStart, join room, produce
 *   accept  -> sfuGroupCallAccept, join room, produce
 *   leave   -> sfuLeaveRoom (+ sfuGroupCallEnd as a safety net)
 *
 * Media handshake, per mediasoup's standard flow:
 *   sfuJoinRoom -> device.load -> createSendTransport / createRecvTransport
 *   -> produce -> consume (paused) -> sfuResume
 */

const CALL_STATUS = {
  IDLE: 'idle',
  CALLING: 'calling',
  INCOMING: 'incoming',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
};

const readStoredDevice = (key) => {
  try {
    return localStorage.getItem(key) || '';
  } catch (_) {
    return '';
  }
};

const useGroupCallSFU = (socket, user, callType = 'audio', { restoreIncoming = true } = {}) => {
  const isVideoCall = callType === 'video';

  const [isCallActive, setIsCallActive] = useState(false);
  const [isIncomingCall, setIsIncomingCall] = useState(false);
  const [callStatus, _setCallStatus] = useState(CALL_STATUS.IDLE);
  const setCallStatus = useCallback((next) => {
    const value = typeof next === 'function' ? next(callStatusRef.current) : next;
    callStatusRef.current = value;
    _setCallStatus(value);
  }, []);
  const [callerId, setCallerId] = useState(null);
  const [callerName, setCallerName] = useState(null);
  const [groupCallId, setGroupCallId] = useState(null);
  const [groupId, setGroupId] = useState(null);
  const [groupName, setGroupName] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [acceptedParticipants, setAcceptedParticipants] = useState(new Set());
  const [localStream, setLocalStream] = useState(null);
  const [error, setError] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [connectionQuality, setConnectionQuality] = useState(new Map());
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [callStartedAt, setCallStartedAt] = useState(null);
  const [incomingRestoreNonce, setIncomingRestoreNonce] = useState(0);

  const [mediaDevices, setMediaDevices] = useState({
    audioInputs: [],
    videoInputs: [],
    audioOutputs: [],
  });
  const [selectedAudioInputId, setSelectedAudioInputId] = useState(() => readStoredDevice('call.audioInputId'));
  const [selectedVideoInputId, setSelectedVideoInputId] = useState(() => readStoredDevice('call.videoInputId'));
  const [selectedAudioOutputId, setSelectedAudioOutputId] = useState(() => readStoredDevice('call.audioOutputId'));
  const [isSwitchingDevice, setIsSwitchingDevice] = useState(false);

  const myId = useMemo(() => {
    const raw = user?.id || user?._id || user;
    return raw?.toString?.() ?? String(raw ?? '');
  }, [user]);

  // ---- mediasoup state ----
  const deviceRef = useRef(null);
  const sendTransportRef = useRef(null);
  const recvTransportRef = useRef(null);
  const producersRef = useRef(new Map()); // Map<kind, producer>
  const consumersRef = useRef(new Map()); // Map<producerId, consumer>
  const producerToUserRef = useRef(new Map()); // Map<producerId, { userId, kind }>
  const remoteStreamsRef = useRef(new Map()); // Map<userId, MediaStream>
  const peerIdsRef = useRef(new Set()); // Map of remote userIds present in the room
  const remoteMuteRef = useRef(new Map()); // Map<userId, { audio: bool, video: bool }>

  // Mirrors of state that socket handlers and async flows must read without
  // being re-created (avoids the stale-closure class of bugs entirely).
  const roomIdRef = useRef(null);
  const groupIdRef = useRef(null);
  const localStreamRef = useRef(null);
  const isCallActiveRef = useRef(false);
  const callStatusRef = useRef(CALL_STATUS.IDLE);
  const cleanupDoneRef = useRef(false);
  const sessionIdRef = useRef(`sfu-${callType}-${Math.random().toString(36).slice(2, 10)}`);
  const leaveCurrentCallRef = useRef(() => {});
  const facingModeRef = useRef('user');
  const durationTimerRef = useRef(null);
  const prevLossRef = useRef(new Map());
  const callStartedAtRef = useRef(null);
  const hadLiveCallRef = useRef(false);
  const resumeAttemptedRef = useRef(false);
  const mountedRef = useRef(true);
  const persistenceKind = isVideoCall ? 'group-video' : 'group-audio';

  const setGroupCallIdSafe = useCallback((id) => {
    const next = id == null || id === '' ? null : String(id);
    roomIdRef.current = next;
    setGroupCallId(next);
  }, []);

  useEffect(() => {
    roomIdRef.current = groupCallId;
  }, [groupCallId]);
  useEffect(() => {
    groupIdRef.current = groupId;
  }, [groupId]);
  useEffect(() => {
    isCallActiveRef.current = isCallActive;
  }, [isCallActive]);
  useEffect(() => {
    callStatusRef.current = callStatus;
  }, [callStatus]);

  // ============================================================
  // Timers
  // ============================================================

  const stopCallTimer = useCallback(() => {
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
  }, []);

  const startCallTimer = useCallback((startedAt) => {
    if (durationTimerRef.current) return;
    const origin = typeof startedAt === 'number'
      ? startedAt
      : (callStartedAtRef.current || Date.now());
    if (!callStartedAtRef.current) {
      callStartedAtRef.current = origin;
      setCallStartedAt(origin);
    }
    const tick = () => setCallDuration(Math.max(0, Math.round((Date.now() - origin) / 1000)));
    tick();
    durationTimerRef.current = setInterval(tick, 1000);
  }, []);

  const formatDuration = useCallback((seconds) => {
    const s = Math.max(0, Number(seconds || 0));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m.toString().padStart(2, '0')}:${r.toString().padStart(2, '0')}`;
  }, []);

  // ============================================================
  // Devices
  // ============================================================

  const refreshMediaDevices = useCallback(async () => {
    try {
      if (!navigator.mediaDevices?.enumerateDevices) return;
      const devices = await navigator.mediaDevices.enumerateDevices();
      setMediaDevices({
        audioInputs: devices.filter((d) => d.kind === 'audioinput'),
        videoInputs: devices.filter((d) => d.kind === 'videoinput'),
        audioOutputs: devices.filter((d) => d.kind === 'audiooutput'),
      });
    } catch (err) {
      console.warn('Error enumerating devices:', err);
    }
  }, []);

  useEffect(() => {
    refreshMediaDevices();
    const handler = () => refreshMediaDevices();
    try {
      navigator.mediaDevices?.addEventListener?.('devicechange', handler);
    } catch (_) {}
    return () => {
      try {
        navigator.mediaDevices?.removeEventListener?.('devicechange', handler);
      } catch (_) {}
    };
  }, [refreshMediaDevices]);

  const persistSelectedDevices = useCallback((next = {}) => {
    try {
      if ('audioInputId' in next) localStorage.setItem('call.audioInputId', next.audioInputId || '');
      if ('videoInputId' in next) localStorage.setItem('call.videoInputId', next.videoInputId || '');
      if ('audioOutputId' in next) localStorage.setItem('call.audioOutputId', next.audioOutputId || '');
    } catch (_) {}
  }, []);

  const setAudioOutputDevice = useCallback(
    (deviceId) => {
      setSelectedAudioOutputId(deviceId || '');
      persistSelectedDevices({ audioOutputId: deviceId || '' });
    },
    [persistSelectedDevices]
  );

  // ============================================================
  // Roster
  // ============================================================

  const applyParticipants = useCallback(() => {
    const list = [];
    for (const uid of peerIdsRef.current) {
      if (!uid || uid === myId) continue;
      const mute = remoteMuteRef.current.get(uid) || {};
      list.push({
        userId: uid,
        stream: remoteStreamsRef.current.get(uid) || null,
        audioMuted: !!mute.audio,
        videoMuted: !!mute.video,
      });
    }
    setParticipants(list);
  }, [myId]);

  const forgetPeer = useCallback(
    (userId) => {
      const uid = userId?.toString();
      if (!uid) return;

      for (const [producerId, info] of producerToUserRef.current.entries()) {
        if (info?.userId !== uid) continue;
        try {
          consumersRef.current.get(producerId)?.close();
        } catch (_) {}
        consumersRef.current.delete(producerId);
        producerToUserRef.current.delete(producerId);
      }

      remoteStreamsRef.current.delete(uid);
      remoteMuteRef.current.delete(uid);
      peerIdsRef.current.delete(uid);
      applyParticipants();

      setAcceptedParticipants((prev) => {
        if (!prev.has(uid)) return prev;
        const next = new Set(prev);
        next.delete(uid);
        return next;
      });
    },
    [applyParticipants]
  );

  // ============================================================
  // Media acquisition
  // ============================================================

  const buildConstraints = useCallback(() => {
    const audio = {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      ...(selectedAudioInputId ? { deviceId: { exact: selectedAudioInputId } } : {}),
    };

    if (!isVideoCall) return { audio, video: false };

    return {
      audio,
      video: {
        width: { ideal: 1280 },
        height: { ideal: 720 },
        ...(selectedVideoInputId
          ? { deviceId: { exact: selectedVideoInputId } }
          : { facingMode: facingModeRef.current }),
      },
    };
  }, [isVideoCall, selectedAudioInputId, selectedVideoInputId]);

  const stopLocalMedia = useCallback(() => {
    try {
      localStreamRef.current?.getTracks().forEach((t) => {
        try {
          t.stop();
        } catch (_) {}
      });
    } catch (_) {}
    localStreamRef.current = null;
    setLocalStream(null);
  }, []);

  const acquireLocalMedia = useCallback(async () => {
    stopLocalMedia();
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia(buildConstraints());
    } catch (err) {
      // A missing/blocked camera should still allow an audio-only join rather
      // than failing the whole call.
      if (isVideoCall) {
        console.warn('Camera unavailable, falling back to audio only:', err?.message || err);
        stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      } else {
        throw err;
      }
    }
    localStreamRef.current = stream;
    setLocalStream(stream);
    setIsMuted(false);
    setIsCameraOff(isVideoCall ? stream.getVideoTracks().length === 0 : false);
    return stream;
  }, [buildConstraints, isVideoCall, stopLocalMedia]);

  // ============================================================
  // mediasoup plumbing
  // ============================================================

  const request = useCallback(
    (event, payload) =>
      new Promise((resolve, reject) => {
        if (!socket?.connected) {
          reject(new Error('Not connected to the server'));
          return;
        }
        let settled = false;
        const timer = setTimeout(() => {
          if (settled) return;
          settled = true;
          reject(new Error(`${event} timed out`));
        }, 15000);

        socket.emit(event, payload, (ack) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          if (ack?.ok) resolve(ack);
          else reject(new Error(ack?.error || `${event} failed`));
        });
      }),
    [socket]
  );

  const closeMediasoup = useCallback(() => {
    for (const consumer of consumersRef.current.values()) {
      try {
        consumer.close();
      } catch (_) {}
    }
    consumersRef.current.clear();

    for (const producer of producersRef.current.values()) {
      try {
        producer.close();
      } catch (_) {}
    }
    producersRef.current.clear();

    try {
      sendTransportRef.current?.close();
    } catch (_) {}
    sendTransportRef.current = null;

    try {
      recvTransportRef.current?.close();
    } catch (_) {}
    recvTransportRef.current = null;

    deviceRef.current = null;
    producerToUserRef.current.clear();
    remoteStreamsRef.current.clear();
    remoteMuteRef.current.clear();
    peerIdsRef.current.clear();
    prevLossRef.current.clear();
  }, []);

  const createTransport = useCallback(
    async (roomId, device, direction) => {
      const res = await request('sfuCreateTransport', { roomId, direction });
      const options = {
        id: res.id,
        iceParameters: res.iceParameters,
        iceCandidates: res.iceCandidates,
        dtlsParameters: res.dtlsParameters,
      };

      const transport =
        direction === 'send' ? device.createSendTransport(options) : device.createRecvTransport(options);

      transport.on('connect', ({ dtlsParameters }, callback, errback) => {
        request('sfuConnectTransport', { roomId, transportId: transport.id, dtlsParameters })
          .then(() => callback())
          .catch(errback);
      });

      if (direction === 'send') {
        transport.on('produce', ({ kind, rtpParameters, appData }, callback, errback) => {
          request('sfuProduce', { roomId, transportId: transport.id, kind, rtpParameters, appData })
            .then((ack) => callback({ id: ack.producerId }))
            .catch(errback);
        });
      }

      transport.on('connectionstatechange', (state) => {
        if (state === 'failed') {
          console.warn(`SFU ${direction} transport failed`);
          setError('Media connection failed. Check your network and try again.');
        }
      });

      return transport;
    },
    [request]
  );

  const consumeProducer = useCallback(
    async ({ roomId, producerId, userId, kind, paused }) => {
      if (!deviceRef.current || !recvTransportRef.current) return;
      if (!producerId || consumersRef.current.has(producerId)) return;

      const uid = userId?.toString();
      if (!uid || uid === myId) return;

      producerToUserRef.current.set(producerId, { userId: uid, kind });

      let res;
      try {
        res = await request('sfuConsume', {
          roomId,
          transportId: recvTransportRef.current.id,
          producerId,
          rtpCapabilities: deviceRef.current.rtpCapabilities,
        });
      } catch (err) {
        console.warn('Consume failed:', err?.message || err);
        producerToUserRef.current.delete(producerId);
        return;
      }

      const consumer = await recvTransportRef.current.consume({
        id: res.consumerId,
        producerId: res.producerId,
        kind: res.kind,
        rtpParameters: res.rtpParameters,
      });
      consumersRef.current.set(producerId, consumer);

      // The server created the consumer paused; resume only once the track is
      // attached, otherwise the first keyframe can be dropped.
      const stream = remoteStreamsRef.current.get(uid) || new MediaStream();
      try {
        stream.addTrack(consumer.track);
      } catch (_) {}
      remoteStreamsRef.current.set(uid, stream);
      peerIdsRef.current.add(uid);

      if (paused || res.producerPaused) {
        const mute = remoteMuteRef.current.get(uid) || {};
        remoteMuteRef.current.set(uid, { ...mute, [res.kind]: true });
      }

      try {
        await request('sfuResume', { roomId, consumerId: consumer.id });
      } catch (err) {
        console.warn('Resume failed:', err?.message || err);
      }

      applyParticipants();
    },
    [request, myId, applyParticipants]
  );

  const produceLocalTracks = useCallback(async (stream) => {
    const transport = sendTransportRef.current;
    if (!transport || !stream) return;

    for (const track of stream.getTracks()) {
      if (producersRef.current.has(track.kind)) continue;
      try {
        const producer = await transport.produce({
          track,
          appData: { kind: track.kind },
        });
        producersRef.current.set(track.kind, producer);
      } catch (err) {
        console.warn(`Failed to publish ${track.kind} track:`, err?.message || err);
      }
    }
  }, []);

  const joinRoom = useCallback(
    async (roomId) => {
      const join = await request('sfuJoinRoom', { roomId, userId: myId });

      const device = new Device();
      await device.load({ routerRtpCapabilities: join.routerRtpCapabilities });
      deviceRef.current = device;

      sendTransportRef.current = await createTransport(roomId, device, 'send');
      recvTransportRef.current = await createTransport(roomId, device, 'recv');

      // Peers already in the room, including any who have not published yet.
      (join.peers || []).forEach((peerId) => {
        const uid = peerId?.toString();
        if (uid && uid !== myId) peerIdsRef.current.add(uid);
      });
      applyParticipants();

      for (const p of join.existingProducers || []) {
        await consumeProducer({
          roomId,
          producerId: p.producerId,
          userId: p.userId,
          kind: p.kind,
          paused: p.paused,
        });
      }
    },
    [request, myId, createTransport, consumeProducer, applyParticipants]
  );

  // ============================================================
  // Call lifecycle
  // ============================================================

  const resetState = useCallback(() => {
    stopCallTimer();
    isCallActiveRef.current = false;
    callStatusRef.current = CALL_STATUS.IDLE;
    roomIdRef.current = null;
    setIsCallActive(false);
    setIsIncomingCall(false);
    setCallStatus(CALL_STATUS.IDLE);
    setParticipants([]);
    setAcceptedParticipants(new Set());
    setCallDuration(0);
    setConnectionQuality(new Map());
    setIsMuted(false);
    setIsCameraOff(false);
    setGroupCallIdSafe(null);
    callStartedAtRef.current = null;
    setCallStartedAt(null);
  }, [stopCallTimer, setGroupCallIdSafe]);

  const teardown = useCallback(() => {
    closeMediasoup();
    stopLocalMedia();
    resetState();
  }, [closeMediasoup, stopLocalMedia, resetState]);

  const leaveCurrentCall = useCallback(() => {
    clearActiveCall(persistenceKind);
    const roomId = roomIdRef.current;
    const status = callStatusRef.current;
    const wasLive =
      isCallActiveRef.current || ['calling', 'connecting', 'connected'].includes(status);
    if (!wasLive && !roomId) return;
    if (cleanupDoneRef.current && !wasLive) return;
    cleanupDoneRef.current = true;
    try {
      if (roomId) {
        socket?.emit('sfuLeaveRoom', { roomId }, () => {});
        socket?.emit(
          'sfuGroupCallEnd',
          {
            groupId: groupIdRef.current,
            from: myId,
            groupCallId: roomId,
            callType,
          },
          () => {}
        );
      }
    } catch (_) {}
    teardown();
  }, [socket, myId, callType, teardown, persistenceKind]);

  useEffect(() => {
    leaveCurrentCallRef.current = leaveCurrentCall;
  }, [leaveCurrentCall]);

  useEffect(() => {
    return registerGroupCallSession({
      id: sessionIdRef.current,
      callType,
      isActive: () => {
        const status = callStatusRef.current;
        return (
          isCallActiveRef.current ||
          ['incoming', 'calling', 'connecting', 'connected'].includes(status)
        );
      },
      getRoomId: () => roomIdRef.current,
      leave: () => leaveCurrentCallRef.current?.(),
    });
  }, [callType]);

  const releaseOtherGroupCalls = useCallback(async () => {
    const left = await leaveOtherGroupCallSessions(sessionIdRef.current);
    try {
      window.dispatchEvent(new CustomEvent('endOtherCallTypes', { detail: { acceptedType: 'group' } }));
    } catch (_) {}
    if (left > 0) {
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
  }, []);

  const startGroupCall = useCallback(
    async (group, memberIds) => {
      const targetGroupId = (group?._id || group?.id)?.toString();
      if (!targetGroupId) {
        setError('Please select a group to call');
        return;
      }

      await releaseOtherGroupCalls();

      const activeCallId = `group-${callType}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      cleanupDoneRef.current = false;
      setError(null);
      setGroupCallIdSafe(activeCallId);
      roomIdRef.current = activeCallId;
      setGroupId(targetGroupId);
      setGroupName(group?.name || 'Group');
      setCallStatus(CALL_STATUS.CALLING);

      try {
        const stream = await acquireLocalMedia();

        const invitees = (Array.isArray(memberIds) ? memberIds : [])
          .map((id) => (typeof id === 'string' ? id : id?._id?.toString?.() || id?.toString?.() || String(id)))
          .filter((id) => id && id !== myId);

        await request('sfuGroupCallStart', {
          groupId: targetGroupId,
          groupName: group?.name,
          from: myId,
          callerName: user?.username || 'Unknown',
          groupCallId: activeCallId,
          callType,
          memberIds: invitees,
        });

        // Join before anyone answers so the room exists and early joiners have
        // our tracks waiting for them.
        await joinRoom(activeCallId);
        await produceLocalTracks(stream);

        setIsCallActive(true);
      } catch (err) {
        console.error('Failed to start group call:', err);
        setError(err?.message || 'Failed to start the group call');
        teardown();
      }
    },
    [callType, myId, user, acquireLocalMedia, request, joinRoom, produceLocalTracks, teardown, releaseOtherGroupCalls, setGroupCallIdSafe]
  );

  const acceptGroupCall = useCallback(async () => {
    await releaseOtherGroupCalls();

    const roomId = roomIdRef.current;
    if (!roomId) {
      setError('This call is no longer available');
      return;
    }

    cleanupDoneRef.current = false;
    setError(null);
    setIsIncomingCall(false);
    setCallStatus(CALL_STATUS.CONNECTING);

    try {
      const stream = await acquireLocalMedia();

      await request('sfuGroupCallAccept', {
        from: myId,
        to: callerId,
        groupCallId: roomId,
        callType,
      });

      await joinRoom(roomId);
      await produceLocalTracks(stream);

      setIsCallActive(true);
      setCallStatus(CALL_STATUS.CONNECTED);
      startCallTimer();
    } catch (err) {
      console.error('Failed to join group call:', err);
      setError(err?.message || 'Failed to join the group call');
      teardown();
    }
  }, [
    acquireLocalMedia,
    request,
    myId,
    callerId,
    callType,
    joinRoom,
    produceLocalTracks,
    startCallTimer,
    teardown,
    releaseOtherGroupCalls,
  ]);

  const joinOngoingGroupCall = useCallback(
    async ({ groupCallId, groupId: gid, groupName: gname, callerId: cid } = {}) => {
      const roomId = groupCallId?.toString();
      if (!roomId) {
        setError('This call is no longer available');
        return;
      }

      await releaseOtherGroupCalls();

      cleanupDoneRef.current = false;
      setError(null);
      setGroupCallIdSafe(roomId);
      setGroupId(gid || null);
      groupIdRef.current = gid || null;
      setGroupName(gname || 'Group');
      setCallerId(cid || null);
      setIsIncomingCall(false);
      setCallStatus(CALL_STATUS.CONNECTING);

      try {
        const stream = await acquireLocalMedia();

        await request('sfuGroupCallAccept', {
          from: myId,
          to: cid,
          groupCallId: roomId,
          callType,
        });

        await joinRoom(roomId);
        await produceLocalTracks(stream);

        setIsCallActive(true);
        setCallStatus(CALL_STATUS.CONNECTED);
        startCallTimer();
      } catch (err) {
        console.error('Failed to join ongoing group call:', err);
        setError(err?.message || 'Failed to join the group call');
        teardown();
        throw err;
      }
    },
    [
      acquireLocalMedia,
      request,
      myId,
      callType,
      joinRoom,
      produceLocalTracks,
      startCallTimer,
      teardown,
      releaseOtherGroupCalls,
      setGroupCallIdSafe,
    ]
  );

  const rejectGroupCall = useCallback(() => {
    const roomId = roomIdRef.current;
    try {
      socket?.emit('sfuGroupCallReject', { from: myId, to: callerId, groupCallId: roomId, callType }, () => {});
    } catch (_) {}
    teardown();
  }, [socket, myId, callerId, callType, teardown]);

  /** Local cleanup only — used on unmount and when the server says the call ended. */
  const handleEndCall = useCallback(() => {
    // A refresh unmounts this hook. Leaving the room here would hang up the call
    // before the reloaded page can rejoin it.
    if (isPageUnloading()) return;
    if (cleanupDoneRef.current) return;
    cleanupDoneRef.current = true;
    clearActiveCall(persistenceKind);
    const roomId = roomIdRef.current;
    try {
      if (roomId) socket?.emit('sfuLeaveRoom', { roomId }, () => {});
    } catch (_) {}
    teardown();
  }, [socket, teardown, persistenceKind]);

  /** User pressed hang up. */
  const endGroupCall = useCallback(() => {
    if (cleanupDoneRef.current) return;
    cleanupDoneRef.current = true;
    clearActiveCall(persistenceKind);
    const roomId = roomIdRef.current;
    try {
      if (roomId) {
        socket?.emit('sfuLeaveRoom', { roomId }, () => {});
        // Safety net for the case where we never made it into the SFU room.
        socket?.emit('sfuGroupCallEnd', { groupId, from: myId, groupCallId: roomId, callType }, () => {});
      }
    } catch (_) {}
    teardown();
  }, [socket, groupId, myId, callType, teardown, persistenceKind]);

  // ============================================================
  // Local controls
  // ============================================================

  // Mute pauses the producer as well as the local track, so muted media is not
  // uploaded to the SFU and remote participants get a mute indicator.
  const toggleMute = useCallback(async () => {
    const track = localStreamRef.current?.getAudioTracks?.()[0];
    if (!track) return;

    const nextMuted = track.enabled;
    track.enabled = !nextMuted;
    setIsMuted(nextMuted);

    const producer = producersRef.current.get('audio');
    const roomId = roomIdRef.current;
    if (!producer || !roomId) return;

    try {
      if (nextMuted) {
        producer.pause();
        await request('sfuPauseProducer', { roomId, kind: 'audio' });
      } else {
        producer.resume();
        await request('sfuResumeProducer', { roomId, kind: 'audio' });
      }
    } catch (err) {
      console.warn('Failed to sync mute state:', err?.message || err);
    }
  }, [request]);

  const toggleCamera = useCallback(async () => {
    const track = localStreamRef.current?.getVideoTracks?.()[0];
    if (!track) return;

    const nextOff = track.enabled;
    track.enabled = !nextOff;
    setIsCameraOff(nextOff);

    const producer = producersRef.current.get('video');
    const roomId = roomIdRef.current;
    if (!producer || !roomId) return;

    try {
      if (nextOff) {
        producer.pause();
        await request('sfuPauseProducer', { roomId, kind: 'video' });
      } else {
        producer.resume();
        await request('sfuResumeProducer', { roomId, kind: 'video' });
      }
    } catch (err) {
      console.warn('Failed to sync camera state:', err?.message || err);
    }
  }, [request]);

  const replaceLocalTrack = useCallback((kind, newTrack) => {
    const current = localStreamRef.current;
    const next = new MediaStream();
    try {
      current?.getTracks().forEach((t) => {
        if (t.kind === kind) {
          try {
            t.stop();
          } catch (_) {}
        } else {
          next.addTrack(t);
        }
      });
    } catch (_) {}
    if (newTrack) next.addTrack(newTrack);
    localStreamRef.current = next;
    setLocalStream(next);
  }, []);

  const switchMicrophone = useCallback(
    async (deviceId) => {
      setSelectedAudioInputId(deviceId || '');
      persistSelectedDevices({ audioInputId: deviceId || '' });
      if (!isCallActiveRef.current) return;

      setIsSwitchingDevice(true);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
          },
          video: false,
        });
        const track = stream.getAudioTracks()[0];
        if (!track) return;
        await producersRef.current.get('audio')?.replaceTrack({ track });
        replaceLocalTrack('audio', track);
        await refreshMediaDevices();
      } catch (err) {
        console.error('Failed to switch microphone:', err);
        setError(err?.message || 'Failed to switch microphone');
      } finally {
        setIsSwitchingDevice(false);
      }
    },
    [persistSelectedDevices, refreshMediaDevices, replaceLocalTrack]
  );

  const switchCamera = useCallback(
    async (deviceId) => {
      setSelectedVideoInputId(deviceId || '');
      persistSelectedDevices({ videoInputId: deviceId || '' });
      if (!isVideoCall || !isCallActiveRef.current) return;

      setIsSwitchingDevice(true);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            ...(deviceId ? { deviceId: { exact: deviceId } } : { facingMode: 'user' }),
          },
          audio: false,
        });
        const track = stream.getVideoTracks()[0];
        if (!track) return;
        await producersRef.current.get('video')?.replaceTrack({ track });
        replaceLocalTrack('video', track);
        await refreshMediaDevices();
      } catch (err) {
        console.error('Failed to switch camera:', err);
        setError(err?.message || 'Failed to switch camera');
      } finally {
        setIsSwitchingDevice(false);
      }
    },
    [isVideoCall, persistSelectedDevices, refreshMediaDevices, replaceLocalTrack]
  );

  const flipCamera = useCallback(async () => {
    if (!isVideoCall || !isCallActiveRef.current) return;

    const nextMode = facingModeRef.current === 'user' ? 'environment' : 'user';
    const oldTrack = localStreamRef.current?.getVideoTracks?.()[0];
    const oldSettings = oldTrack?.getSettings?.() || {};

    setIsSwitchingDevice(true);
    // Release the camera before requesting the other one; mobile hardware
    // usually refuses to open both at once.
    try {
      oldTrack?.stop();
    } catch (_) {}

    const base = { width: { ideal: 1280 }, height: { ideal: 720 } };
    const attempt = async (video) => {
      const stream = await navigator.mediaDevices.getUserMedia({ video, audio: false });
      const track = stream.getVideoTracks()[0];
      if (!track) return false;
      await producersRef.current.get('video')?.replaceTrack({ track });
      replaceLocalTrack('video', track);
      facingModeRef.current = nextMode;
      return true;
    };

    try {
      try {
        if (await attempt({ ...base, facingMode: { exact: nextMode } })) return;
      } catch (_) {}

      let cameras = [];
      try {
        cameras = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === 'videoinput');
      } catch (_) {}

      const pattern = nextMode === 'environment' ? /back|rear|environment/i : /front|user|facetime/i;
      let target = cameras.find((c) => pattern.test(c.label));
      if (!target && cameras.length > 1) target = cameras.find((c) => c.deviceId !== oldSettings.deviceId);
      if (target) {
        try {
          if (await attempt({ ...base, deviceId: { exact: target.deviceId } })) return;
        } catch (_) {}
      }

      try {
        if (await attempt({ ...base, facingMode: nextMode })) return;
      } catch (_) {}

      // Nothing worked — restore the camera we just released.
      await attempt(
        oldSettings.deviceId
          ? { ...base, deviceId: { exact: oldSettings.deviceId } }
          : { ...base, facingMode: facingModeRef.current }
      );
      setError('Could not switch camera on this device');
    } catch (err) {
      console.error('Failed to flip camera:', err);
      setError(`Failed to flip camera: ${err?.message || err}`);
    } finally {
      setIsSwitchingDevice(false);
    }
  }, [isVideoCall, replaceLocalTrack]);

  // ============================================================
  // SFU socket events
  // ============================================================

  useEffect(() => {
    if (!socket) return undefined;

    const inCurrentRoom = (roomId) => {
      const current = roomIdRef.current?.toString();
      return !!current && roomId?.toString() === current;
    };

    const onPeerJoined = ({ roomId, userId }) => {
      if (!inCurrentRoom(roomId)) return;
      const uid = userId?.toString();
      if (!uid || uid === myId) return;

      peerIdsRef.current.add(uid);
      applyParticipants();

      // The caller stays on the outgoing screen until somebody is actually in
      // the room. A refresh can still be "connecting" when that join arrives,
      // so both states have to leave the ringing screen.
      if (
        callStatusRef.current === CALL_STATUS.CALLING ||
        callStatusRef.current === CALL_STATUS.CONNECTING
      ) {
        setCallStatus(CALL_STATUS.CONNECTED);
        startCallTimer();
      }
    };

    const onNewProducer = async ({ roomId, producerId, userId, kind, paused }) => {
      if (!inCurrentRoom(roomId)) return;
      const uid = userId?.toString();
      if (
        uid &&
        uid !== myId &&
        (callStatusRef.current === CALL_STATUS.CALLING ||
          callStatusRef.current === CALL_STATUS.CONNECTING)
      ) {
        setCallStatus(CALL_STATUS.CONNECTED);
        startCallTimer();
      }
      await consumeProducer({ roomId, producerId, userId, kind, paused });
    };

    const onProducerClosed = ({ roomId, producerId, userId }) => {
      if (roomId && !inCurrentRoom(roomId)) return;
      const info = producerToUserRef.current.get(producerId);
      const uid = (userId || info?.userId)?.toString();

      try {
        consumersRef.current.get(producerId)?.close();
      } catch (_) {}
      consumersRef.current.delete(producerId);
      producerToUserRef.current.delete(producerId);

      if (uid) {
        const stream = remoteStreamsRef.current.get(uid);
        if (stream) {
          // Drop tracks that no longer have a live consumer behind them.
          stream.getTracks().forEach((track) => {
            if (track.readyState === 'ended') {
              try {
                stream.removeTrack(track);
              } catch (_) {}
            }
          });
          if (stream.getTracks().length === 0) remoteStreamsRef.current.delete(uid);
        }
        applyParticipants();
      }
    };

    const onPeerLeft = ({ roomId, userId }) => {
      if (!inCurrentRoom(roomId)) return;
      forgetPeer(userId);
    };

    const setRemoteMute = (userId, kind, muted) => {
      const uid = userId?.toString();
      if (!uid) return;
      const current = remoteMuteRef.current.get(uid) || {};
      remoteMuteRef.current.set(uid, { ...current, [kind]: muted });
      applyParticipants();
    };

    const onProducerPaused = ({ roomId, producerId, userId, kind }) => {
      if (roomId && !inCurrentRoom(roomId)) return;
      const info = producerToUserRef.current.get(producerId);
      setRemoteMute(userId || info?.userId, kind || info?.kind, true);
    };

    const onProducerResumed = ({ roomId, producerId, userId, kind }) => {
      if (roomId && !inCurrentRoom(roomId)) return;
      const info = producerToUserRef.current.get(producerId);
      setRemoteMute(userId || info?.userId, kind || info?.kind, false);
    };

    socket.on('sfuPeerJoined', onPeerJoined);
    socket.on('sfuNewProducer', onNewProducer);
    socket.on('sfuProducerClosed', onProducerClosed);
    socket.on('sfuPeerLeft', onPeerLeft);
    socket.on('sfuProducerPaused', onProducerPaused);
    socket.on('sfuProducerResumed', onProducerResumed);

    return () => {
      socket.off('sfuPeerJoined', onPeerJoined);
      socket.off('sfuNewProducer', onNewProducer);
      socket.off('sfuProducerClosed', onProducerClosed);
      socket.off('sfuPeerLeft', onPeerLeft);
      socket.off('sfuProducerPaused', onProducerPaused);
      socket.off('sfuProducerResumed', onProducerResumed);
    };
  }, [socket, myId, consumeProducer, applyParticipants, forgetPeer, startCallTimer]);

  // Rejoin after a socket reconnect: the server dropped our peer when the
  // socket died, so transports and producers must be rebuilt from scratch.
  useEffect(() => {
    if (!socket) return undefined;

    const onConnect = async () => {
      const roomId = roomIdRef.current;
      if (!isCallActiveRef.current || !roomId || !localStreamRef.current) return;

      console.log('Socket reconnected — rejoining group call');
      setCallStatus(CALL_STATUS.CONNECTING);
      try {
        closeMediasoup();
        await joinRoom(roomId);
        await produceLocalTracks(localStreamRef.current);
        setCallStatus(CALL_STATUS.CONNECTED);
      } catch (err) {
        console.error('Failed to rejoin group call:', err);
        setError('Lost connection to the call');
        handleEndCall();
      }
    };

    socket.on('connect', onConnect);
    return () => socket.off('connect', onConnect);
  }, [socket, closeMediasoup, joinRoom, produceLocalTracks, handleEndCall]);

  // ============================================================
  // Connection quality (packet loss per remote participant)
  // ============================================================

  useEffect(() => {
    if (callStatus !== CALL_STATUS.CONNECTED) return undefined;

    const interval = setInterval(async () => {
      const next = new Map();

      for (const [producerId, consumer] of consumersRef.current.entries()) {
        const uid = producerToUserRef.current.get(producerId)?.userId;
        if (!uid || consumer.closed) continue;

        try {
          const stats = await consumer.getStats();
          stats.forEach((report) => {
            if (report.type !== 'inbound-rtp') return;
            const entry = next.get(uid) || { packetsLost: 0, rtt: 0 };
            const key = `${uid}:${producerId}`;
            const total = report.packetsLost || 0;
            // Report loss for this window only; the raw counter is cumulative
            // and would eventually mark every call as poor.
            const delta = Math.max(0, total - (prevLossRef.current.get(key) || 0));
            prevLossRef.current.set(key, total);

            next.set(uid, {
              packetsLost: entry.packetsLost + delta,
              rtt: Math.max(entry.rtt, Math.round((report.jitter || 0) * 1000)),
            });
          });
        } catch (_) {}
      }

      setConnectionQuality(next);
    }, 3000);

    return () => clearInterval(interval);
  }, [callStatus]);

  const buildGroupSnapshot = useCallback(() => {
    const status = callStatusRef.current;
    const roomId = roomIdRef.current;
    if (!roomId) return null;
    if (status === CALL_STATUS.INCOMING) {
      return {
        kind: persistenceKind,
        userId: myId,
        groupCallId: roomId,
        callId: roomId,
        status: 'incoming',
        groupId: groupIdRef.current,
        groupName,
        callerId,
        callerName,
        direction: 'incoming',
        connectedAt: null,
      };
    }
    if (!['calling', 'connecting', 'connected'].includes(status)) return null;
    return {
      kind: persistenceKind,
      userId: myId,
      groupCallId: roomId,
      callId: roomId,
      status,
      groupId: groupIdRef.current,
      groupName,
      callerId,
      callerName,
      connectedAt: callStartedAtRef.current,
      isMuted,
      isCameraOff,
    };
  }, [persistenceKind, myId, groupName, callerId, callerName, isMuted, isCameraOff]);

  const buildGroupSnapshotRef = useRef(buildGroupSnapshot);
  buildGroupSnapshotRef.current = buildGroupSnapshot;

  useEffect(() => {
    if (callStatus === 'connected' && !callStartedAtRef.current) {
      const origin = Date.now();
      callStartedAtRef.current = origin;
      setCallStartedAt(origin);
    }
  }, [callStatus]);

  useEffect(() => {
    const active = ['incoming', 'calling', 'connecting', 'connected'].includes(callStatus);
    if (active) {
      hadLiveCallRef.current = true;
      const snapshot = buildGroupSnapshot();
      if (snapshot) saveActiveCall(snapshot);
      return;
    }
    if (hadLiveCallRef.current && !isPageUnloading()) {
      hadLiveCallRef.current = false;
      clearActiveCall(persistenceKind);
    }
  }, [callStatus, buildGroupSnapshot, persistenceKind]);

  useEffect(() => {
    const flush = () => {
      const snapshot = buildGroupSnapshotRef.current?.();
      if (snapshot) saveActiveCall(snapshot);
    };
    window.addEventListener('beforeunload', flush);
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('beforeunload', flush);
      window.removeEventListener('pagehide', flush);
    };
  }, []);

  const resumeGroupCall = useCallback(async (snapshot) => {
    const roomId = snapshot.groupCallId?.toString();
    if (!roomId) throw new Error('Missing call');

    cleanupDoneRef.current = false;
    setError(snapshot.connectedAt ? 'Reconnecting…' : null);
    setGroupCallIdSafe(roomId);
    roomIdRef.current = roomId;
    setGroupId(snapshot.groupId || null);
    groupIdRef.current = snapshot.groupId || null;
    setGroupName(snapshot.groupName || 'Group');
    setCallerId(snapshot.callerId || null);
    setCallerName(snapshot.callerName || null);
    if (snapshot.connectedAt) {
      callStartedAtRef.current = snapshot.connectedAt;
      setCallStartedAt(snapshot.connectedAt);
    }
    setIsCallActive(true);
    isCallActiveRef.current = true;
    setCallStatus(CALL_STATUS.CONNECTING);

    await request('sfuGroupCallAccept', {
      from: myId,
      to: snapshot.callerId,
      groupCallId: roomId,
      callType,
      resume: true,
    });

    const stream = await acquireLocalMedia();
    if (snapshot.isMuted) {
      stream.getAudioTracks().forEach((track) => { track.enabled = false; });
      setIsMuted(true);
    }
    if (snapshot.isCameraOff) {
      stream.getVideoTracks().forEach((track) => { track.enabled = false; });
      setIsCameraOff(true);
    }

    await joinRoom(roomId);
    await produceLocalTracks(stream);

    if (snapshot.isMuted) {
      try {
        producersRef.current.get('audio')?.pause();
        await request('sfuPauseProducer', { roomId, kind: 'audio' });
      } catch (_) {}
    }
    if (snapshot.isCameraOff) {
      try {
        producersRef.current.get('video')?.pause();
        await request('sfuPauseProducer', { roomId, kind: 'video' });
      } catch (_) {}
    }

    const someoneElse = peerIdsRef.current.size > 0;
    const alreadyLive = callStatusRef.current === CALL_STATUS.CONNECTED;
    if (someoneElse || snapshot.connectedAt || alreadyLive) {
      if (!alreadyLive) setCallStatus(CALL_STATUS.CONNECTED);
      startCallTimer(snapshot.connectedAt || undefined);
    } else {
      setCallStatus(CALL_STATUS.CALLING);
    }
    setError(null);
  }, [
    request,
    myId,
    callType,
    acquireLocalMedia,
    joinRoom,
    produceLocalTracks,
    startCallTimer,
    setGroupCallIdSafe,
  ]);

  useEffect(() => {
    if (!socket || !myId || resumeAttemptedRef.current) return undefined;
    const preview = readActiveCall();
    if (!preview || preview.kind !== persistenceKind) return undefined;
    if (preview.userId && preview.userId !== myId) return undefined;
    if (preview.status === 'incoming' && !restoreIncoming) return undefined;
    resumeAttemptedRef.current = true;
    const snapshot = consumeActiveCall(persistenceKind, myId);
    if (!snapshot?.groupCallId) return undefined;

    if (snapshot.status === 'incoming') {
      setCallerId(snapshot.callerId || null);
      setCallerName(snapshot.callerName || null);
      setGroupId(snapshot.groupId || null);
      groupIdRef.current = snapshot.groupId || null;
      setGroupName(snapshot.groupName || 'Group');
      setGroupCallIdSafe(snapshot.groupCallId);
      setCallStatus(CALL_STATUS.INCOMING);
      setIsIncomingCall(true);
      setIncomingRestoreNonce((n) => n + 1);
      return undefined;
    }

    (async () => {
      const ready = await waitForSocket(socket);
      if (!mountedRef.current) return;
      if (!ready) {
        setError('Could not reconnect to the call');
        clearActiveCall(persistenceKind);
        return;
      }
      try {
        await resumeGroupCall(snapshot);
      } catch (err) {
        console.error('Failed to rejoin group call:', err);
        if (!mountedRef.current) return;
        setError(err?.message === 'Call ended' ? 'The call has ended' : 'Could not reconnect to the call');
        handleEndCall();
      }
    })();
    return undefined;
  }, [socket, myId, persistenceKind, resumeGroupCall, handleEndCall, restoreIncoming, setGroupCallIdSafe]);

  // Release everything if the component goes away mid-call.
  const handleEndCallRef = useRef(handleEndCall);
  useEffect(() => {
    handleEndCallRef.current = handleEndCall;
  }, [handleEndCall]);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (isPageUnloading()) return;
      handleEndCallRef.current?.();
    };
  }, []);

  return {
    // State
    isCallActive,
    setIsCallActive,
    isIncomingCall,
    setIsIncomingCall,
    callStatus,
    setCallStatus,
    callerId,
    setCallerId,
    callerName,
    setCallerName,
    groupCallId,
    setGroupCallId: setGroupCallIdSafe,
    groupId,
    setGroupId,
    groupName,
    setGroupName,
    participants,
    setParticipants,
    acceptedParticipants,
    setAcceptedParticipants,
    localStream,
    error,
    setError,
    callDuration,
    connectionQuality,
    isMuted,
    isCameraOff,
    callStartedAt,
    incomingRestoreNonce,

    // Devices
    mediaDevices,
    refreshMediaDevices,
    selectedAudioInputId,
    selectedVideoInputId,
    selectedAudioOutputId,
    isSwitchingDevice,
    switchMicrophone,
    switchCamera,
    flipCamera,
    setAudioOutputDevice,

    // Actions
    startGroupCall,
    acceptGroupCall,
    joinOngoingGroupCall,
    rejectGroupCall,
    endGroupCall,
    leaveCurrentCall,
    handleEndCall,
    handleParticipantLeft: forgetPeer,
    toggleMute,
    toggleCamera,
    startCallTimer,
    formatDuration,
  };
};

export default useGroupCallSFU;
